import { PACKAGE_VERSION } from "./constants";
import ExitManager from "./exit";
import { initProvider } from "./provider";
import CONV from "./semantic-conventions";
import { SessionManager } from "./sessions/SessionManager";
import { SessionSpanProcessor } from "./sessions/SessionSpanProcessor";
import { getCurrentSessionId, getPreviousSessionId, setCurrentUser } from "./sessions/store";
import { UserInfo } from "./sessions/types";
import { OTELOptions } from "./types";
import { getSampleRateRatio } from "./utils";
import { setGlobalSpanAttributes } from "./utils/globalSpanAttributes";
import { error, log, setLogsEnabled, warn } from "./utils/logger";
import { sanitizeSpanAttributes } from "./utils/spanAttributes";
import { ViewRootSpanManager } from "./view";

type RumLogLevel = "info" | "warn" | "error";

const emitRumLog = (
  level: RumLogLevel,
  message: string,
  attributes: Record<string, unknown> = {},
  err?: unknown,
) => {
  const payload = {
    message,
    attributes: {
      "l9rum.sdk.component": "Rum",
      ...attributes,
    },
    ...(err ? { error: err } : {}),
  };

  if (level === "warn") {
    warn(payload);
    return;
  }

  if (level === "error") {
    error(payload);
    return;
  }

  log(payload);
};

export class Rum {
  private isInited: boolean = false;
  private viewManager?: ViewRootSpanManager;
  constructor() {
    this.isInited = false;
    this.viewManager = undefined;
  }

  public async init(options: OTELOptions) {
    const {
      debug,
      debugLogs,
      headers,
      baseUrl,
      resourceAttributes,
      sampleRate,
      maxExportBatchSize,
      interactions,
      errors,
      network,
      geoIpEnabled,
    } = options;

    // Decide head-based sampling for the entire session before any instrumentation starts.
    const sessionSampleRatio = getSampleRateRatio(sampleRate);
    const sessionSampled = Math.random() < sessionSampleRatio;

    // Enable/disable logs early
    const providerOptions = {
      headers,
      baseUrl,
      resourceAttributes,
      // Force 100% trace sampling for sampled sessions to avoid partial sessions.
      // When session is not sampled, we won't initialize the provider at all.
      sampleRate: 100,
      maxExportBatchSize,
      geoIpEnabled,
    };

    setLogsEnabled(!!debugLogs);
    if (this.isInited) {
      emitRumLog("warn", "[L9RUM] init called more than once; ignoring", {
        "l9rum.sdk.phase": "init",
        "l9rum.sdk.guard": "duplicate_init",
      });
      return false;
    }

    this.viewManager = undefined;

    emitRumLog("info", "[L9RUM] Initialization started", {
      "l9rum.sdk.phase": "init",
      "l9rum.sdk.guard": "start",
      "l9rum.sdk.debug": debug ? "true" : "false",
      "l9rum.sdk.sampling.ratio": sessionSampleRatio,
    });

    // If the session is not sampled, short-circuit: do not initialize provider or any instrumentation.
    if (!sessionSampled) {
      try {
        emitRumLog("info", "[L9RUM] Session not sampled; RUM disabled for this session", {
          "l9rum.sdk.phase": "init",
          "l9rum.sdk.guard": "head_sampling",
          "l9rum.sdk.sampling.decision": "drop",
        });
      } catch {}
      // Mark as initialized to avoid duplicate init attempts; nothing else to set up.
      this.viewManager = undefined;
      this.isInited = true;
      return true;
    }

    try {
      const provider = await initProvider({
        ...providerOptions,
        extraSpanProcessors: [new SessionSpanProcessor()],
      });
      const sessionManager = new SessionManager(provider);
      sessionManager.start();

      const viewManager = new ViewRootSpanManager(provider, { interactions, errors, network });
      this.viewManager = viewManager;
      try {
        viewManager.init();
      } catch (e) {
        emitRumLog(
          "error",
          "[L9RUM] View initialization failed",
          {
            "l9rum.sdk.phase": "init",
            "l9rum.sdk.guard": "view_init",
          },
          e,
        );
      }

      const exitManager = new ExitManager(provider, viewManager, sessionManager);
      exitManager.init();

      this.viewManager = viewManager;
      this.isInited = true;
      emitRumLog("info", "[L9RUM] Initialization completed", {
        "l9rum.sdk.phase": "init",
        "l9rum.sdk.guard": "complete",
        "l9rum.sdk.version": PACKAGE_VERSION,
      });
      return true;
    } catch (e) {
      this.isInited = false;
      this.viewManager = undefined;
      emitRumLog(
        "error",
        "[L9RUM] Initialization failed",
        {
          "l9rum.sdk.phase": "init",
          "l9rum.sdk.guard": "init_failed",
        },
        e,
      );
      return false;
    }
  }

  public identify(user?: UserInfo | null) {
    // If identify is called without a user or with a falsy value, clear the user
    if (!user || typeof user !== "object") {
      setCurrentUser(undefined);
      emitRumLog("info", "[L9RUM] identify cleared user", {
        "l9rum.sdk.phase": "identify",
        "l9rum.sdk.guard": "empty_user",
      });
      return;
    }

    // Only keep provided fields
    const clean: UserInfo = {
      id: user.id,
      name: user.name,
      fullName: user.fullName,
      email: user.email,
      roles: Array.isArray(user.roles) ? user.roles.filter(Boolean) : undefined,
    };

    // Remove empty values
    Object.keys(clean).forEach((k) => {
      const key = k as keyof UserInfo;
      const val = clean[key];
      if (
        val === undefined ||
        val === null ||
        val === "" ||
        (Array.isArray(val) && val.length === 0)
      ) {
        delete (clean as any)[key];
      }
    });
    setCurrentUser(Object.keys(clean).length ? clean : undefined);
    emitRumLog("info", "[L9RUM] identify processed user", {
      "l9rum.sdk.phase": "identify",
      "l9rum.sdk.guard": "set_user",
      "l9rum.sdk.user_provided_keys": JSON.stringify(Object.keys(user || {})),
      "l9rum.sdk.user_stored_keys": JSON.stringify(Object.keys(clean || {})),
    });
  }

  public clearUser() {
    setCurrentUser(undefined);
    emitRumLog("info", "[L9RUM] clearUser called", {
      "l9rum.sdk.phase": "clear_user",
    });
  }

  public captureError(error: unknown, context: Record<string, any> = {}) {
    if (!this.viewManager) {
      emitRumLog("warn", "[L9RUM] captureError called before initialization", {
        "l9rum.sdk.phase": "capture_error",
        "l9rum.sdk.guard": "no_view_manager",
      });
      return;
    }

    try {
      this.viewManager.captureError(error, context);
    } catch (e) {
      emitRumLog(
        "warn",
        "[L9RUM] Failed to capture error",
        {
          "l9rum.sdk.phase": "capture_error",
          "l9rum.sdk.guard": "capture_failed",
        },
        e,
      );
    }
  }

  public addEvent(eventName: string, attributes: Record<string, unknown> = {}) {
    const trimmedName = typeof eventName === "string" ? eventName.trim() : "";

    if (!trimmedName) {
      emitRumLog("warn", "[L9RUM] addEvent called without a valid event name", {
        "l9rum.sdk.phase": "add_event",
        "l9rum.sdk.guard": "invalid_name",
      });
      return;
    }

    if (!this.isInited) {
      emitRumLog("warn", "[L9RUM] addEvent called before init", {
        "l9rum.sdk.phase": "add_event",
        "l9rum.sdk.guard": "uninitialized",
      });
      return;
    }

    try {
      const sanitizedAttributes = sanitizeSpanAttributes(attributes);

      const sessionId = getCurrentSessionId();
      const previousSessionId = getPreviousSessionId();
      if (sessionId) {
        sanitizedAttributes[CONV.SESSION_ID] = sessionId;
      }
      if (previousSessionId) {
        sanitizedAttributes[CONV.SESSION_PREVIOUS_ID] = previousSessionId;
      }

      const timestamp = Date.now();

      if (!this.viewManager) {
        emitRumLog("warn", "[L9RUM] addEvent skipped because view manager is unavailable", {
          "l9rum.sdk.phase": "add_event",
          "l9rum.sdk.guard": "view_manager_unavailable",
        });
        return;
      }

      this.viewManager.recordCustomEvent(trimmedName, sanitizedAttributes, timestamp);

      emitRumLog("info", "[L9RUM] addEvent recorded custom event", {
        "l9rum.sdk.phase": "add_event",
        "l9rum.sdk.guard": "complete",
        "l9rum.sdk.event_name": trimmedName,
        "l9rum.sdk.attribute_count": Object.keys(sanitizedAttributes).length,
      });
    } catch (err) {
      emitRumLog(
        "error",
        "[L9RUM] addEvent failed to record custom event",
        {
          "l9rum.sdk.phase": "add_event",
          "l9rum.sdk.guard": "exception",
          "l9rum.sdk.event_name": trimmedName,
        },
        err,
      );
    }
  }

  public spanAttributes(attributes?: Record<string, unknown> | null) {
    const isPlainRecord =
      !!attributes && typeof attributes === "object" && !Array.isArray(attributes);
    const sanitized = setGlobalSpanAttributes(attributes);

    if (!isPlainRecord) {
      emitRumLog("info", "[L9RUM] spanAttributes cleared global attributes", {
        "l9rum.sdk.phase": "span_attributes",
        "l9rum.sdk.guard": attributes ? "invalid_input" : "cleared",
      });
      return;
    }

    const providedKeys = Object.keys(attributes || {});
    const storedKeys = Object.keys(sanitized);

    emitRumLog("info", "[L9RUM] spanAttributes stored attributes", {
      "l9rum.sdk.phase": "span_attributes",
      "l9rum.sdk.guard": storedKeys.length ? "stored" : "empty_after_sanitize",
      "l9rum.sdk.attributes_provided_keys": JSON.stringify(providedKeys),
      "l9rum.sdk.attributes_stored_keys": JSON.stringify(storedKeys),
    });
  }
}
